import React, { useState, useEffect, useRef } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Input, Select, Button, Spinner } from "./ui";
import { formatMoney } from "../lib/utils";
import {
  ASSET_TYPES, DIVIDEND_TYPES, INTEREST_TYPES, OPTION_SIDES, TRANSFER_KINDS,
} from "../lib/portfolio";
import { Check } from "lucide-react";

const POS = "#10B981";
export const num = (v) => (v === "" || v == null ? null : Number(v));

// Chip-style single-select used for asset type / dividend type / sides etc.
export function Chips({ options, value, onChange, lang, testid }) {
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={testid}>
      {options.map((o) => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          data-testid={`${testid}-${o.value}`}
          className={`px-2.5 py-1 rounded-full border text-xs transition-all ${value === o.value ? "text-text-primary" : "text-text-secondary"}`}
          style={value === o.value ? { borderColor: "#2563EB", background: "#2563EB1a" } : {}}>
          {lang === "fr" ? o.label.fr : o.label.en}
        </button>
      ))}
    </div>
  );
}

export function Labeled({ label, children }) {
  return (
    <div>
      <label className="text-xs text-text-secondary block mb-1">{label}</label>
      {children}
    </div>
  );
}

export function AccountSelect({ accounts, value, onChange, testid, placeholder }) {
  const { t } = useApp();
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      <option value="">{placeholder || t("portfolio_no_account")}</option>
      {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
    </Select>
  );
}

export const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "CAD", "JPY"];

export function CurrencyField({ value, onChange, testid }) {
  const { t } = useApp();
  return (
    <Labeled label={t("activity_currency")}>
      <Select value={value || "EUR"} onChange={(e) => onChange(e.target.value)} data-testid={testid || "activity-currency"}>
        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </Select>
    </Labeled>
  );
}

// Reusable symbol autocomplete + live-price lookup.
export function SymbolField({ region, value, onChange, onPick, placeholder, withCheck = true, testid }) {
  const { t } = useApp();
  const [results, setResults] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [lookup, setLookup] = useState(null);
  const typed = useRef(false);

  useEffect(() => {
    // only search when the user is actively typing (not when the value is set
    // programmatically, e.g. picking from the holdings quick-picker)
    if (!typed.current) { setResults([]); setShowDrop(false); return undefined; }
    const q = (value || "").trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const tid = setTimeout(async () => {
      try {
        const { data } = await api.get(`/portfolio/symbol-search?q=${encodeURIComponent(q)}&region=${region}`);
        setResults(data); setShowDrop(true);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(tid);
  }, [value, region]);

  const doLookup = async (sym) => {
    setLookup("loading");
    try {
      const { data } = await api.get(`/portfolio/symbol-lookup?symbol=${encodeURIComponent(sym)}`);
      setLookup(data);
      return data;
    } catch { setLookup("error"); return null; }
  };

  const pick = async (r) => {
    typed.current = false;
    onChange(r.symbol);
    setShowDrop(false); setResults([]);
    const d = await doLookup(r.symbol);
    onPick?.(r, d?.price ?? null, d?.currency ?? null);
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <Input value={value} autoComplete="off"
          onChange={(e) => { typed.current = true; onChange(e.target.value.toUpperCase()); setLookup(null); }}
          onFocus={() => results.length && setShowDrop(true)}
          placeholder={placeholder || t("portfolio_symbol_search_hint")} className="flex-1 font-mono" data-testid={`${testid}-input`} />
        {withCheck && (
          <Button variant="outline" onClick={async () => { if (value.trim()) { const d = await doLookup(value.trim()); onPick?.({ symbol: value.trim() }, d?.price ?? null, d?.currency ?? null); } }}
            disabled={!value.trim() || lookup === "loading"} data-testid={`${testid}-check`}>
            {lookup === "loading" ? <Spinner className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          </Button>
        )}
      </div>
      {showDrop && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded-lg border bg-surface shadow-lg" data-testid={`${testid}-results`}>
          {results.map((r) => (
            <button key={r.symbol} onClick={() => pick(r)} data-testid={`${testid}-result-${r.symbol}`}
              className="w-full text-left px-3 py-2 hover:bg-surface-hover transition-colors flex items-center justify-between gap-2">
              <span className="min-w-0">
                <span className="font-mono text-sm">{r.symbol}</span>
                <span className="block text-[11px] text-text-secondary truncate">{r.name}</span>
              </span>
              <span className="text-[10px] text-text-secondary shrink-0">{r.exchange}</span>
            </button>
          ))}
        </div>
      )}
      {lookup && lookup !== "loading" && lookup !== "error" && (
        <p className="text-[11px] mt-1" style={{ color: POS }} data-testid={`${testid}-lookup`}>
          {t("portfolio_price_verified")}: {formatMoney(lookup.price, lookup.currency, { decimals: 2 })}
        </p>
      )}
      {lookup === "error" && <p className="text-[11px] text-text-secondary mt-1" data-testid={`${testid}-lookup`}>{t("portfolio_manual_hint")}</p>}
    </div>
  );
}

function SymField({ form, set, region, onSymbolPick, ...extra }) {
  return (
    <SymbolField region={region} value={form.symbol}
      onChange={(v) => set("symbol", v)} onPick={onSymbolPick} testid="activity-symbol" {...extra} />
  );
}

// Quick-pick of holdings the user already owns (filtered to the selected account).
// Saves typing the symbol on a repeat buy / a sell. Shows the held quantity.
export function HoldingPicker({ positions, accountId, onPick, t }) {
  const list = (positions || []).filter(
    (p) => p.symbol && p.quantity > 0 && (!accountId || p.account_id === accountId));
  if (!list.length) return null;
  return (
    <Labeled label={t("activity_your_holdings")}>
      <div className="flex gap-1.5 flex-wrap" data-testid="activity-holding-picker">
        {list.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p)}
            data-testid={`activity-holding-pick-${p.symbol}`}
            className="px-2.5 py-1 rounded-full border text-xs text-text-secondary hover:text-text-primary transition-all"
            style={{ borderColor: "var(--border)" }}>
            <span className="font-mono text-text-primary">{p.symbol}</span>
            <span className="ml-1 opacity-70">· {p.quantity}</span>
          </button>
        ))}
      </div>
    </Labeled>
  );
}

// ----------------------- type-specific form sections -----------------------
export function TradeFields({ form, set, t, lang, region, onSymbolPick, positions, onHoldingPick }) {
  const isOption = form.asset_type === "option";
  return (
    <>
      <Chips testid="activity-asset-type" lang={lang} value={form.asset_type}
        onChange={(v) => set("asset_type", v)} options={ASSET_TYPES} />
      {isOption ? (
        <>
          <div className="text-xs font-medium text-text-secondary">{t("activity_option_contract")}</div>
          <Labeled label={t("portfolio_symbol")}>
            <SymField form={form} set={set} region={region} onSymbolPick={onSymbolPick} placeholder={t("activity_option_search_hint")} />
          </Labeled>
          <Chips testid="activity-option-side" lang={lang} value={form.option_side}
            onChange={(v) => set("option_side", v)} options={OPTION_SIDES} />
          <div className="grid grid-cols-2 gap-3">
            <Labeled label={t("activity_strike")}>
              <Input type="number" value={form.strike} onChange={(e) => set("strike", e.target.value)} data-testid="activity-strike-input" />
            </Labeled>
            <Labeled label={t("activity_expiration")}>
              <Input type="date" value={form.expiration} onChange={(e) => set("expiration", e.target.value)} data-testid="activity-expiration-input" />
            </Labeled>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Labeled label={t("activity_contracts")}>
              <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="activity-quantity-input" />
            </Labeled>
            <Labeled label={t("activity_premium")}>
              <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="activity-price-input" />
            </Labeled>
            <Labeled label={t("activity_fee")}>
              <Input type="number" value={form.fee} onChange={(e) => set("fee", e.target.value)} data-testid="activity-fee-input" />
            </Labeled>
          </div>
        </>
      ) : (
        <>
          <HoldingPicker positions={positions} accountId={form.account_id} onPick={onHoldingPick} t={t} />
          <Labeled label={t("portfolio_symbol")}>
            <SymField form={form} set={set} region={region} onSymbolPick={onSymbolPick} />
          </Labeled>
          <Labeled label={t("portfolio_name")}>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Apple / Fonds Euro…" data-testid="activity-name-input" />
          </Labeled>
          <div className="grid grid-cols-3 gap-3">
            <Labeled label={t("portfolio_quantity")}>
              <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="activity-quantity-input" />
            </Labeled>
            <Labeled label={t("portfolio_price")}>
              <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="activity-price-input" />
            </Labeled>
            <Labeled label={t("activity_fee")}>
              <Input type="number" value={form.fee} onChange={(e) => set("fee", e.target.value)} data-testid="activity-fee-input" />
            </Labeled>
          </div>
          {form.asset_type === "bond" && (
            <div className="grid grid-cols-2 gap-3">
              <Labeled label={t("activity_coupon")}>
                <Input type="number" value={form.coupon} onChange={(e) => set("coupon", e.target.value)} data-testid="activity-coupon-input" />
              </Labeled>
              <Labeled label={t("activity_maturity")}>
                <Input type="date" value={form.expiration} onChange={(e) => set("expiration", e.target.value)} data-testid="activity-maturity-input" />
              </Labeled>
            </div>
          )}
        </>
      )}
    </>
  );
}

export function SplitFields({ form, set, t, region, onSymbolPick }) {
  return (
    <>
      <Labeled label={t("portfolio_symbol")}>
        <SymField form={form} set={set} region={region} onSymbolPick={onSymbolPick} withCheck={false} />
      </Labeled>
      <Labeled label="Ratio">
        <Input type="number" value={form.ratio} onChange={(e) => set("ratio", e.target.value)} placeholder="2 = 2:1" data-testid="activity-split-ratio-input" />
      </Labeled>
    </>
  );
}

export function DividendFields({ form, set, t, lang, region, onSymbolPick }) {
  return (
    <>
      <Labeled label={t("activity_dividend_type")}>
        <Chips testid="activity-dividend-type" lang={lang} value={form.dividend_type}
          onChange={(v) => set("dividend_type", v)} options={DIVIDEND_TYPES} />
      </Labeled>
      <Labeled label={t("activity_asset")}>
        <SymField form={form} set={set} region={region} onSymbolPick={onSymbolPick} />
      </Labeled>
      {form.dividend_type === "cash" ? (
        <Labeled label={t("activity_amount")}>
          <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="activity-amount-input" />
        </Labeled>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Labeled label={t("activity_shares_received")}>
            <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="activity-quantity-input" />
          </Labeled>
          <Labeled label={t("portfolio_price")}>
            <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="activity-price-input" />
          </Labeled>
        </div>
      )}
    </>
  );
}

export function InterestFields({ form, set, t, lang, region, onSymbolPick }) {
  return (
    <>
      <Labeled label={t("activity_interest_type")}>
        <Chips testid="activity-interest-type" lang={lang} value={form.interest_type}
          onChange={(v) => set("interest_type", v)} options={INTEREST_TYPES} />
      </Labeled>
      <Labeled label={`${t("portfolio_symbol")} ${t("optional")}`}>
        <SymField form={form} set={set} region={region} onSymbolPick={onSymbolPick} withCheck={false} />
      </Labeled>
      <Labeled label={t("activity_amount")}>
        <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="activity-amount-input" />
      </Labeled>
    </>
  );
}

export function CashAmountField({ form, set, t }) {
  return (
    <Labeled label={t("activity_amount")}>
      <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="activity-amount-input" />
    </Labeled>
  );
}

export function TransferFields({ form, set, t, lang, accounts, region, onSymbolPick }) {
  return (
    <div className="space-y-3">
      <Chips testid="activity-transfer-kind" lang={lang} value={form.transfer_kind}
        onChange={(v) => set("transfer_kind", v)} options={TRANSFER_KINDS} />
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="activity-external-transfer">
        <input type="checkbox" checked={form.is_external} onChange={(e) => set("is_external", e.target.checked)} />
        {t("activity_external_transfer")}
      </label>
      {form.is_external ? (
        <>
          <Labeled label={t("portfolio_account")}>
            <AccountSelect accounts={accounts} value={form.account_id} onChange={(v) => set("account_id", v)} testid="activity-account-select" />
          </Labeled>
          <Chips testid="activity-ext-dir" lang={lang} value={form.ext_dir} onChange={(v) => set("ext_dir", v)}
            options={[{ value: "in", label: { fr: "Entrée", en: "Money in" } }, { value: "out", label: { fr: "Sortie", en: "Money out" } }]} />
        </>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Labeled label={t("activity_from_account")}>
            <AccountSelect accounts={accounts} value={form.from_account_id} onChange={(v) => set("from_account_id", v)} testid="activity-from-account" placeholder={t("activity_select_source")} />
          </Labeled>
          <Labeled label={t("activity_to_account")}>
            <AccountSelect accounts={accounts} value={form.to_account_id} onChange={(v) => set("to_account_id", v)} testid="activity-to-account" placeholder={t("activity_select_dest")} />
          </Labeled>
        </div>
      )}
      <Labeled label={t("activity_date")}>
        <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="activity-date-input" />
      </Labeled>
      {form.transfer_kind === "securities" ? (
        <>
          <Labeled label={t("portfolio_symbol")}>
            <SymField form={form} set={set} region={region} onSymbolPick={onSymbolPick} />
          </Labeled>
          <Labeled label={t("portfolio_quantity")}>
            <Input type="number" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="activity-quantity-input" />
          </Labeled>
        </>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Labeled label={t("activity_amount")}>
            <Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="activity-amount-input" />
          </Labeled>
          <CurrencyField value={form.currency} onChange={(v) => set("currency", v)} />
        </div>
      )}
    </div>
  );
}
